import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { Review } from '@vibesites/schema';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { btnOnDark } from '../utils/buttonStyles';
import { ctaLinkProps } from '../utils/scheduleTourModal';

const DISPLAY_LIMIT = 3;
const MIN_RATING = 4;
const BANNED_WORD_PATTERN = /\b(clean|safe|quiet)\b/i;

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function parseRating(rating: string | null | undefined): number {
  if (rating == null) return 0;
  const n = parseFloat(String(rating));
  return Number.isFinite(n) ? n : 0;
}

function mentionsBannedWords(review: Review): boolean {
  const haystack = [review.text, review.title].filter(hasValue).join(' ');
  return BANNED_WORD_PATTERN.test(haystack);
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Reviews Page Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/reviews',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Read All Reviews' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function StarIcon({ filled, className }: { filled: boolean; className?: string }) {
  return (
    <svg
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function renderStars(rating: number) {
  const filledCount = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${filledCount} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon
          key={i}
          filled={i < filledCount}
          className={`w-4 h-4 ${i < filledCount ? 'text-amber-400' : 'text-[var(--vibe-color-border)]'}`}
        />
      ))}
    </div>
  );
}

export function ReviewsCallout() {
  const ctaLink = useSlotGroup('reviewscallout.cta_link', ctaLinkDef);

  const reviews = useCollectionBinding<Review>('reviewscallout.items', {
    collection: 'reviews',
  });

  const displayReviews = useMemo(() => {
    const filtered = (reviews || [])
      .filter(
        (review) =>
          parseRating(review.rating) >= MIN_RATING &&
          !mentionsBannedWords(review),
      )
      .sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return db - da;
      });
    return filtered.slice(0, DISPLAY_LIMIT);
  }, [reviews]);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  return (
    <section className="py-16 md:py-24 bg-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-12">
          <RichTextContent
            path="reviewscallout.title"
            label="Title"
            defaultValue='<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>'
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-primary-foreground uppercase tracking-wide"
          />
          <div className="w-16 h-1 mx-auto mt-6 rounded-full bg-primary-foreground/40" />
        </div>

        <EditableCollectionBinding bindingPath="reviewscallout.items" label="Reviews">
          {displayReviews.length > 0 ? (
            <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {displayReviews.map((review, index) => {
                const numericRating = parseRating(review.rating);
                return (
                  <div
                    key={review.id || `${review.name || 'review'}-${index}`}
                    data-reveal
                    className="reveal-fade-up bg-white p-8 flex flex-col"
                  >
                    <div className="mb-4">{renderStars(numericRating)}</div>

                    {hasValue(review.text) && (
                      <p className="font-body text-sm text-[var(--vibe-color-text)] mb-6 flex-grow leading-relaxed line-clamp-4">
                        &ldquo;{review.text}&rdquo;
                      </p>
                    )}

                    <div className="mt-auto pt-4 border-t border-[var(--vibe-color-border)]">
                      {hasValue(review.name) && (
                        <p className="font-body text-sm font-semibold text-[var(--vibe-color-text)]">
                          {review.name}
                        </p>
                      )}
                      {hasValue(review.date) && (
                        <p className="text-xs text-[var(--vibe-color-text-muted)] mt-1">
                          {review.date}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 mb-12 text-primary-foreground/60">
              <p className="font-body text-lg">Resident reviews will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>

        <div className="text-center">
          <EditableSlotGroup groupPath="reviewscallout.cta_link" definition={ctaLinkDef}>
            <a
              {...ctaLinkProps(ctaLink.url || '/reviews', ctaLink._target)}
              className={`inline-flex items-center px-8 py-3 text-sm font-medium tracking-widest uppercase ${btnOnDark}`}
            >
              {ctaLink.label || 'Read All Reviews'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsCallout',
  displayName: 'Reviews Callout',
  tokens: {
    'reviewscallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'reviewscallout.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'reviewscallout.items': {
      label: 'Reviews',
      description:
        "Reviews from the property's selected source rated 4 stars and above, excluding mentions of clean, safe, or quiet",
      collection: 'reviews',
    },
  },
};

(ReviewsCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ReviewsCallout;
